/**
 * Client-portal API client - the client reads its own CRM data (invoices,
 * quotes, projects, tickets, contracts) and answers the two things that ask
 * something of them: a quote and a contract. The scope comes from the server
 * (`Contact.userId -> Client`); no client id is ever sent from here. PDFs open
 * as direct links (`/api/...` proxy + session cookie), see `pdfHref`.
 */
import { api } from "./api";
import { withPreview } from "./previewMode";
import type { PortalModule } from "@wa/core/portal-modules";
import type { PortalProjectTab } from "@wa/core/portal-project-tabs";
import type { EstimateAnswerState, EstimateDecision } from "@wa/core/estimate-answer";

export interface PortalClientRef {
  id: string;
  name: string;
}

export interface PortalSummary {
  /** True when this user is a contact at at least one company. */
  linked: boolean;
  clients: PortalClientRef[];
  /**
   * The modules the server will actually answer for, in tab order.
   *
   * NOT derived from `linked`. A person can be linked and granted nothing, and
   * the two facts drive different parts of the shell: `linked` decides whether
   * this browser is looking at a portal at all, `modules` decides which doors
   * are drawn. Reading tabs off `linked` alone is how a client ends up clicking
   * Invoices and getting an empty list the server was never going to fill.
   */
  modules: PortalModule[];
  stats: {
    outstandingCents: number;
    overdueInvoices: number;
    openTickets: number;
    activeProjects: number;
    /** Quotes still waiting for this client's answer. */
    pendingEstimates: number;
  };
}

export interface PortalInvoice {
  id: string;
  number: string;
  title: string | null;
  status: string;
  currency: string;
  totalCents: number;
  paidCents: number;
  issueDate: string;
  dueDate: string | null;
  client: PortalClientRef;
}

export interface PortalLineItem {
  description?: string;
  /** The paragraph under the line's name, plain text with newlines kept. */
  details?: string;
  qty?: number;
  unitPriceCents?: number;
  taxRate?: number;
  [k: string]: unknown;
}

export interface PortalPayment {
  amountCents: number;
  currency: string;
  method: string | null;
  reference: string | null;
  paidAt: string;
}

/** The answer to "can this invoice be paid by card right now". */
export interface PortalPayable {
  available: boolean;
  outstandingCents: number;
  currency: string;
  /**
   * Present only when `available` is false.
   *
   * `method_off` is the desk having switched card payment off in Settings, and
   * it is deliberately a different answer from the three below: nothing is wrong
   * with the invoice. The button is hidden either way, which is why no screen
   * renders this string today - it is here so a support question has an answer.
   */
  reason?: "not_payable" | "already_settled" | "stripe_not_configured" | "method_off";
}

export interface PortalInvoiceDetail extends PortalInvoice {
  lineItems: PortalLineItem[];
  subtotalCents: number;
  discountCents: number;
  taxCents: number;
  /**
   * Whether this document says anything about VAT. Decided by the server, which
   * is the only side that knows whether the seller charges it today; a document
   * issued while they did keeps its row either way. Optional because a cached
   * bundle can outlive the API - absent means yes, the old behaviour.
   */
  showVat?: boolean;
  notes: string | null;
  terms: string | null;
  payments: PortalPayment[];
}

export interface PortalEstimate {
  id: string;
  number: string;
  title: string | null;
  status: string;
  currency: string;
  totalCents: number;
  validUntil: string | null;
  createdAt: string;
  client: PortalClientRef;
  /**
   * May this client accept or decline it right now?
   *
   * Computed by the server, never re-derived here. Nothing ever writes
   * `EstimateStatus.expired`, so a quote that lapsed months ago still arrives
   * with `status: "sent"`; a browser deciding for itself would offer buttons the
   * API then refuses. `answerState` is the same answer with its reason, so the
   * badge can say "expired" where the stored status still says "sent".
   */
  answerable: boolean;
  answerState: EstimateAnswerState;
}

/** Who answered a quote in the portal, and when. Absent on quotes answered at the desk. */
export interface PortalEstimateDecision {
  decision: EstimateDecision;
  name?: string;
  email?: string;
  at?: string;
  note?: string;
}

export interface PortalEstimateDetail extends PortalEstimate {
  lineItems: PortalLineItem[];
  qtyMode: string;
  subtotalCents: number;
  taxCents: number;
  /** See `PortalInvoiceDetail.showVat`. */
  showVat?: boolean;
  notes: string | null;
  acceptedAt: string | null;
  portalDecision: PortalEstimateDecision | null;
}

export interface PortalProject {
  id: string;
  name: string;
  status: string;
  progressPct: number;
  startDate: string | null;
  deadline: string | null;
  completedAt: string | null;
  client: PortalClientRef;
  _count: { tasks: number; milestones: number };
}

/** The delivery record: what was actually built, as the customer may read it. */
export interface PortalProjectOverview {
  platform: string | null;
  logoStatus: string | null;
  services: string[];
  features: string[];
  pages: string | null;
}

export interface PortalProjectDetail {
  id: string;
  name: string;
  description: string | null;
  status: string;
  progressPct: number;
  startDate: string | null;
  deadline: string | null;
  completedAt: string | null;
  client: PortalClientRef;
  /**
   * The tabs the server will answer for, in tab order.
   *
   * Taken from here rather than derived from which arrays came back non-empty.
   * A project with no milestones YET is not a project whose milestones tab was
   * closed, and guessing would hide a granted tab and read as a permission
   * problem. The server owns the answer; this list is it.
   */
  tabs: PortalProjectTab[];
  /** Absent when the overview tab is closed, along with its whole payload. */
  overview?: PortalProjectOverview;
  milestones: { id: string; name: string; dueDate: string | null; done: boolean }[];
  tasks: { id: string; title: string; status: string; priority: string; dueDate: string | null; completedAt: string | null }[];
}

export interface PortalTicket {
  id: string;
  number: string;
  subject: string;
  status: string;
  priority: string;
  department: string | null;
  createdAt: string;
  updatedAt: string;
  client: PortalClientRef;
  _count: { messages: number };
}

export interface PortalTicketMessage {
  id: string;
  authorType: "staff" | "client" | "bot";
  author: string;
  body: string;
  createdAt: string;
}

export interface PortalTicketDetail extends Omit<PortalTicket, "_count"> {
  messages: PortalTicketMessage[];
}

export interface PortalContract {
  id: string;
  number: string | null;
  title: string;
  type: string | null;
  status: string;
  valueCents: number | null;
  currency: string;
  startDate: string | null;
  endDate: string | null;
  signedAt: string | null;
  client: PortalClientRef;
  signable: boolean;
}

export interface PortalContractSignature {
  name: string;
  email?: string;
  ip?: string;
  method?: string;
  signedAt?: string;
  /** Signature drawn on a canvas (PNG data-URL). */
  image?: string;
}

export interface PortalContractDetail extends PortalContract {
  content: unknown;
  signature?: PortalContractSignature | null;
}

/** One line of the account statement. `opening` is the balance carried in. */
export interface PortalStatementRow {
  kind: "opening" | "invoice" | "payment" | "credit_note";
  date: string;
  ref: string | null;
  againstRef: string | null;
  chargeCents: number | null;
  creditCents: number | null;
  balanceCents: number;
}

export interface PortalStatement {
  client: PortalClientRef;
  /** Every company this reader may ask for, so the picker needs no second call. */
  clients: PortalClientRef[];
  currency: string;
  from: string;
  to: string;
  openingCents: number;
  invoicedCents: number;
  paidCents: number;
  closingCents: number;
  rows: PortalStatementRow[];
}

export interface PortalNotificationCategory {
  key: string;
  enabled: boolean;
}
export interface PortalNotificationGroup {
  clientId: string;
  clientName: string;
  categories: PortalNotificationCategory[];
}

/**
 * Direct PDF/file link (the `/api` proxy to :3211, session cookie included).
 *
 * `withPreview` because this is the one door a header cannot reach: a document
 * is opened as a plain `<a href>`, so during a staff preview every PDF would
 * answer 403 while the pages around it rendered - which reads as a broken portal
 * rather than as a boundary. On an ordinary visit it returns the path unchanged.
 */
export function pdfHref(path: string): string {
  return withPreview(`/api${path}`);
}

/**
 * The optional "only this project" filter the four linked lists accept.
 *
 * A NARROWING, not a permission: the server gates each of those lists on its own
 * module whether or not a project id is sent. What it buys is that the project
 * page does not fetch the whole register and sieve it here.
 */
export interface PortalListFilter {
  projectId?: string;
}

function withFilter(path: string, filter?: PortalListFilter): string {
  if (!filter?.projectId) return path;
  return `${path}?projectId=${encodeURIComponent(filter.projectId)}`;
}

export const portal = {
  summary: () => api.get<PortalSummary>("/portal/summary"),

  invoices: (filter?: PortalListFilter) => api.get<PortalInvoice[]>(withFilter("/portal/invoices", filter)),
  invoice: (id: string) => api.get<PortalInvoiceDetail>(`/portal/invoices/${id}`),
  /**
   * Whether a card payment is on offer for this invoice, and for how much.
   *
   * Asked of the server rather than worked out from the status and the two money
   * columns on screen. The rule (which statuses, what happens at a zero balance,
   * whether Stripe is even configured) lives in one place on the API side; a
   * second copy here would be a second thing to keep in step, and its drift
   * would show as a button that leads to a 409.
   */
  invoicePayable: (id: string) => api.get<PortalPayable>(`/portal/invoices/${id}/pay`),
  /** Open a card payment and get the URL to send the browser to. */
  payInvoice: (id: string) => api.post<{ url: string }>(`/portal/invoices/${id}/pay`, {}),
  estimates: (filter?: PortalListFilter) => api.get<PortalEstimate[]>(withFilter("/portal/estimates", filter)),
  estimate: (id: string) => api.get<PortalEstimateDetail>(`/portal/estimates/${id}`),
  answerEstimate: (id: string, decision: EstimateDecision, note?: string) =>
    api.post<PortalEstimateDetail>(`/portal/estimates/${id}/decision`, { decision, ...(note ? { note } : {}) }),

  projects: () => api.get<PortalProject[]>("/portal/projects"),
  project: (id: string) => api.get<PortalProjectDetail>(`/portal/projects/${id}`),

  tickets: (filter?: PortalListFilter) => api.get<PortalTicket[]>(withFilter("/portal/tickets", filter)),
  ticket: (id: string) => api.get<PortalTicketDetail>(`/portal/tickets/${id}`),
  replyTicket: (id: string, body: string) => api.post<PortalTicketDetail>(`/portal/tickets/${id}/messages`, { body }),
  createTicket: (subject: string, body: string) => api.post<PortalTicketDetail>("/portal/tickets", { subject, body }),

  contracts: (filter?: PortalListFilter) => api.get<PortalContract[]>(withFilter("/portal/contracts", filter)),
  contract: (id: string) => api.get<PortalContractDetail>(`/portal/contracts/${id}`),
  signContract: (id: string, signerName: string, signatureImage?: string) =>
    api.post<PortalContractDetail>(`/portal/contracts/${id}/sign`, {
      signerName, consent: true, ...(signatureImage ? { signatureImage } : {}),
    }),

  /**
   * The account statement for ONE company over one window.
   *
   * The company is named rather than inferred when the reader represents more
   * than one: two ledgers summed into one balance would be a number that exists
   * in nobody's books. Omitting `clientId` asks for the first company the server
   * will answer for, which is what the page opens on.
   */
  statement: (opts?: { clientId?: string; from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (opts?.clientId) q.set("clientId", opts.clientId);
    if (opts?.from) q.set("from", opts.from);
    if (opts?.to) q.set("to", opts.to);
    const qs = q.toString();
    return api.get<PortalStatement>(`/portal/statement${qs ? `?${qs}` : ""}`);
  },

  notifications: () => api.get<{ clients: PortalNotificationGroup[] }>("/portal/notifications"),
  setNotifications: (clientId: string, prefs: PortalNotificationCategory[]) =>
    api.put<{ categories: PortalNotificationCategory[] }>("/portal/notifications", { clientId, prefs }),
};
